import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Table, Spin } from 'antd';
import axios from "axios";
import Swal from 'sweetalert2';
import { BASE_URL } from '../../App';
import {
    Box,
    Card as MUICard,
    CardContent,
    Typography as MuiText,
    Select,
    MenuItem,
    Button,
    FormControl,
    InputLabel,
    TextField,
    useMediaQuery,
} from '@mui/material';
import { DownloadOutlined } from "@ant-design/icons";
import { CalendarMonth } from '@mui/icons-material';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';


const { Title } = Typography;

export default function Reports() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [dateType, setDateType] = useState('today');
    const [reportType, setReportType] = useState(null);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [reportVisible, setReportVisible] = useState(false);
    const [selectedRange, setSelectedRange] = useState([]);
    const [Counts, setCounts] = useState({
        TotalCount: 0,
        GivenToCustomer: 0,
        PendingToCustomer: 0,
        GivenToCRI: 0,
        ReceivedFromCRI: 0,
    });
    const [ReportTypeList, setReportTypeList] = useState([]);
    const [columns, setColumns] = useState([]);
    const [ReportData, setReportData] = useState([]);

    // new states for branch and dealer/retailer
    const [branchList, setBranchList] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [dealerList, setDealerList] = useState([]);
    const [selectedDealer, setSelectedDealer] = useState('');

    const login_id = localStorage.getItem("LOGIN_ID");
    const branch_code = localStorage.getItem("BRANCH_CODE");

    useEffect(() => {
        setSelectedBranch(null);
        setSelectedDealer(null);
        setDealerList([]);
        setReportData([]);
        setReportVisible(false);

        LoadReportData();
        fetchBranchList();
        const timer = setTimeout(() => setIsLoading(false), 800);
        return () => clearTimeout(timer);
    }, []);

    const fetchBranchList = () => {
        setIsLoading(true);
        const data = {
            login_id: login_id,
            branch_code: branch_code,
        }

        axios.post(BASE_URL + '/getbranchforreport', data)
            .then(response => {
                setBranchList(response.data.Branchlist);
            }).catch(error => {
                Swal.fire({
                    icon: "warning",
                    text: error?.response?.data?.message || "Error fetching data"
                });
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const getDateRange = (type) => {
        const today = dayjs();
        switch (type) {
            case 'today':
                return [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
            case 'week':
                return [
                    today.startOf('week').format('YYYY-MM-DD'),
                    today.endOf('week').format('YYYY-MM-DD'),
                ];
            case 'month':
                return [
                    today.startOf('month').format('YYYY-MM-DD'),
                    today.endOf('month').format('YYYY-MM-DD'),
                ];
            case 'year':
                return [
                    today.startOf('year').format('YYYY-MM-DD'),
                    today.endOf('year').format('YYYY-MM-DD'),
                ];
            default:
                return ['', ''];
        }
    };

    const LoadReportData = async () => {
        setIsLoading(true);
        try {
            const Token = localStorage.getItem('Token');
            if (!Token) {
                navigate('/logout');
                return;
            }

            const mobile_no = localStorage.getItem('Mobile_number');
            const branch_code = localStorage.getItem('branch_code');
            const data = { user_id: mobile_no, branch_code };
            const headers = { Authorization: `Bearer ${Token}` };

            const response = await axios.post(`${BASE_URL}/ReplacementReportData`, data, { headers });
            setIsLoading(false);

            setReportTypeList(response.data.ReportType || []);
            setDealerList([]);
        } catch (error) {
            setIsLoading(false);
            Swal.fire({
                icon: "error",
                title: "Error!",
                text: error.response?.data?.message || "Something went wrong!",
            });
        }
    };

    const handleBranchChange = async (branchCode) => {
        setSelectedBranch(branchCode);
        setSelectedDealer('');
        setDealerList([]);

        if (!branchCode) return;
        setIsLoading(true);

        const data = {
            login_id: login_id,
            BranchCode: branchCode,
        }

        try {
            const response = await axios.post(`${BASE_URL}/GetReplacementDealerRetailer`, data);
            setDealerList(response.data.Data || []);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            Swal.fire({
                icon: 'error',
                title: 'Error fetching dealers',
                text: error.response?.data?.message || 'Something went wrong!',
            });
        }
    };

    const handleDealerChange = async (dealerCode) => {
        setSelectedDealer(dealerCode);
    };

    const handleViewReport = async () => {
        let from_date, to_date;

        if (dateType !== 'custom') {
            const [start, end] = getDateRange(dateType);
            from_date = start;
            to_date = end;
        } else {
            if (!fromDate || !toDate) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Missing Dates',
                    text: 'Please select both start and end dates.',
                });
                return;
            }

            if (new Date(fromDate) > new Date(toDate)) {
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid Range',
                    text: 'Start date cannot be after end date.',
                });
                return;
            }

            from_date = fromDate;
            to_date = toDate;
        }

        setSelectedRange([from_date, to_date]);
        setReportVisible(true);
        setIsLoading(true);

        try {
            const mobile_no = localStorage.getItem('Mobile_number');
            const branch_code = selectedBranch;

            if (!reportType) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Missing Information',
                    text: 'Please select report type.',
                });
                setIsLoading(false);
                return;
            }

            const payload = {
                user_id: mobile_no,
                branch_code,
                dealer_code: selectedDealer,
                reportType,
                from_date,
                to_date,
            };

            const response = await axios.post(`${BASE_URL}/ReplacementReportView`, payload);
            const rows = response.data?.Data || [];

            if (rows.length > 0) {
                const dynamicColumns = Object.keys(rows[0]).map((key) => ({
                    title: key.replace(/_/g, ' '),
                    dataIndex: key,
                    key,
                }));
                setColumns(dynamicColumns);
                const dataWithKeys = rows.map((item, index) => ({
                    key: index,
                    ...item,
                }));
                setReportData(dataWithKeys);
            } else {
                setColumns([]);
                setReportData([]);
            }

        } catch (error) {
            const message =
                error.response?.data?.message ||
                (error.message?.includes('Network')
                    ? 'Network error. Please check your connection.'
                    : 'Something went wrong!');
            Swal.fire({ icon: 'error', title: 'Error!', text: message });
        } finally {
            setIsLoading(false);
        }
    };

    const isMobile = useMediaQuery('(max-width:600px)');

    const Loader = () => (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                width: "100%",
            }}
        >
            <Spin tip="Loading" size="large" />
        </div>
    );

    const handleDownloadExcel = () => {
        if (!ReportData || ReportData.length === 0) {
            Swal.fire({
                icon: 'info',
                title: 'No Data',
                text: 'No report data available to download.',
            });
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(ReportData.map(({ key, ...rest }) => rest)); // remove React table key
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');

        const fileName = `Report_${reportType || 'Data'}_${selectedRange[0] || 'from'}_to_${selectedRange[1] || 'to'}.xlsx`;
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(data, fileName);
    };


    return (
        <>
            <div className="reports-container">
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className="reports-content">
                        <MUICard
                            sx={{
                                mt: 5,
                                borderRadius: 4,
                                p: 4,
                                background: 'linear-gradient(135deg, #e3f2fd 0%, #f9f9ff 100%)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            }}
                        >
                            <CardContent>
                                <MuiText variant="h5" sx={{ fontWeight: 700, color: '#001529', mb: 1 }}>
                                    Replacement Reports
                                </MuiText>
                                <MuiText variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                    Select filters below to view your customized report.
                                </MuiText>

                                {/* Filters */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                        mb: 4,
                                    }}
                                >

                                    <>
                                        <FormControl sx={{ minWidth: 180 }} size="small">
                                            <InputLabel>Report Type</InputLabel>
                                            <Select
                                                fullWidth
                                                value={reportType}
                                                label="Report Type"
                                                onChange={(e) => setReportType(e.target.value)}
                                            >
                                                {ReportTypeList.length > 0 ? (
                                                    ReportTypeList.map((data) => (
                                                        <MenuItem value={data.Value} key={data.Value}>
                                                            {data.Label}
                                                        </MenuItem>
                                                    ))
                                                ) : (
                                                    <MenuItem value="">No Data Found!</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>

                                        {/* Date Range */}
                                        <FormControl sx={{ minWidth: 160 }}>
                                            <InputLabel>Date Range</InputLabel>
                                            <Select
                                                value={dateType}
                                                label="Date Range"
                                                onChange={(e) => {
                                                    setDateType(e.target.value);
                                                    if (e.target.value !== 'custom') {
                                                        const [start, end] = getDateRange(e.target.value);
                                                        setFromDate(start);
                                                        setToDate(end);
                                                    } else {
                                                        setFromDate('');
                                                        setToDate('');
                                                    }
                                                }}
                                                size="small"
                                            >
                                                <MenuItem value="today">Today</MenuItem>
                                                <MenuItem value="week">This Week</MenuItem>
                                                <MenuItem value="month">This Month</MenuItem>
                                                <MenuItem value="year">This Year</MenuItem>
                                                <MenuItem value="custom">Custom</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {dateType === 'custom' && (
                                            <>
                                                <TextField
                                                    label="From"
                                                    type="date"
                                                    value={fromDate}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    size="small"
                                                />
                                                <TextField
                                                    label="To"
                                                    type="date"
                                                    value={toDate}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                    InputLabelProps={{ shrink: true }}
                                                    size="small"
                                                />
                                            </>
                                        )}

                                    </>
                                    {/* Branch */}
                                    <FormControl sx={{ minWidth: 180 }} size="small">
                                        <InputLabel>Branch</InputLabel>
                                        <Select
                                            value={selectedBranch}
                                            label="Branch"
                                            onChange={(e) => handleBranchChange(e.target.value)}
                                        >
                                            <MenuItem value="">Select Branch</MenuItem>
                                            {branchList.map((branch) => (
                                                <MenuItem key={branch.branch} value={branch.branch}>
                                                    {branch.branch_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* Dealer / Retailer */}
                                    <FormControl sx={{ minWidth: 180 }} size="small" disabled={!selectedBranch}>
                                        <InputLabel>Dealer / Retailer</InputLabel>
                                        <Select
                                            value={selectedDealer}
                                            label="Dealer / Retailer"
                                            onChange={(e) => handleDealerChange(e.target.value)}
                                        >
                                            <MenuItem value="">Select Dealer/Retailer</MenuItem>
                                            {dealerList.map((dealer) => (
                                                <MenuItem key={dealer.Value} value={dealer.Value}>
                                                    {dealer.Label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {/* View Report Button */}
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<CalendarMonth />}
                                        onClick={handleViewReport}
                                        size="small"
                                        sx={{
                                            py: 1,
                                            px: 3,
                                            borderRadius: 3,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #1565c0, #1e88e5)',
                                            },
                                        }}
                                    >
                                        View Report
                                    </Button>
                                    {!reportVisible && (
                                        <>
                                            <br />
                                            <span
                                                style={{
                                                    textAlign: 'center',
                                                    color: '#df1212ff',
                                                }}
                                            >
                                                * If Branch and Dealer/Retailer are not selected, the report will display data only for the selected Date Range.
                                            </span>
                                        </>
                                    )}
                                </Box>

                                {/* Report Display */}
                                <Box>
                                    {reportVisible ? (
                                        <>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 2,
                                                }}
                                            >
                                                <MuiText variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    ({selectedRange[0]} → {selectedRange[1]})
                                                </MuiText>

                                                {ReportData.length > 0 && (
                                                    <Button
                                                        variant="outlined"
                                                        color="success"
                                                        size="small"
                                                        startIcon={<DownloadOutlined />}
                                                        onClick={handleDownloadExcel}
                                                        sx={{
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontWeight: 600,
                                                            borderColor: '#22c55e',
                                                            background: '#22c55e',
                                                            color: '#fff',
                                                            '&:hover': {
                                                                background: '#22c55e',
                                                                color: '#fff',
                                                            },
                                                        }}
                                                    >
                                                        Download Excel
                                                    </Button>
                                                )}
                                            </Box>
                                            {isMobile ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {ReportData.map((item) => (
                                                        <MUICard
                                                            key={item.key}
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 2,
                                                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                                                backgroundColor: 'white',
                                                            }}
                                                        >
                                                            {Object.entries(item).map(([field, value]) =>
                                                                field !== 'key' ? (
                                                                    <MuiText key={field} variant="body2" sx={{ mb: 0.5 }}>
                                                                        <strong>{field.replace(/_/g, ' ')}:</strong> {value || '-'}
                                                                    </MuiText>
                                                                ) : null
                                                            )}
                                                        </MUICard>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Table
                                                    dataSource={ReportData}
                                                    columns={columns}
                                                    pagination={{ pageSize: 50 }}
                                                    rowKey="id"
                                                    bordered
                                                    style={{ background: 'white', borderRadius: 8 }}
                                                    size='small'
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <MuiText variant="body2" color="text.secondary">
                                            Select filters and click “View Report” to display analytics.
                                        </MuiText>
                                    )}
                                </Box>
                            </CardContent>
                        </MUICard>
                    </div>
                )}
            </div>

            {/* Styles */}
            <style>{`
                .reports-container {
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                    width: 100%;
                    padding: 10px;
                }

                .reports-content {
                    width: 100%;
                    padding: 10px;
                    text-align: center;
                }

                .reports-title {
                    color: #0d47a1;
                    font-weight: 800;
                    margin-bottom: 10px;
                }

                .reports-row {
                    margin-top: 20px;
                }

                .reports-card {
                    border-radius: 18px !important;
                    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
                    transition: all 0.35s ease;
                    text-align: center;
                    padding: 12px;
                }

                .reports-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
                }

                .total-card { background: linear-gradient(135deg, #e0f7fa, #b2ebf2); }

                .success-card { background: linear-gradient(135deg, #e8f5e9, #c8e6c9); }

                .warning-card { background: linear-gradient(135deg, #fff8e1, #ffecb3); }

                .info-card { background: linear-gradient(135deg, #e3f2fd, #bbdefb); }

                .received-card { background: linear-gradient(135deg, #e8f5e9, #a5d6a7); }
            `}</style>
        </>
    );
}
